import React, { useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import MUIRadio from '@mui/material/Radio';
import MUIRadioGroup from '@mui/material/RadioGroup';
import MUIFormControlLabel from '@mui/material/FormControlLabel';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import renderController from '../lib/renderController';
import overlay from '../Palettes/overlay';

const RadioGroup = styled(MUIRadioGroup)`
  .MuiRadio-colorSecondary {
    color: ${overlay[600]};
  }

  .MuiRadio-colorSecondary.Mui-checked {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .MuiFormControlLabel-label {
    color: ${overlay[600]};
    transform: none;
    letter-spacing: unset;
    margin: 2px 0 0px;
  }

  .MuiButtonBase-root {
    padding: 3px;
  }
`;

const radioButtonsPropertyTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

export type RadioButtonsProperties = InferProps<
  typeof radioButtonsPropertyTypes
>;

const RadioButtons = ({
  values,
  name,
  value,
  onChange,
}: RadioButtonsProperties) => (
  <RadioGroup
    aria-label={name}
    name={name}
    value={value}
    onChange={onChange}
  >
    {
      values.map(({ value: optionValue, label }) => (
        <MUIFormControlLabel
          key={optionValue}
          control={<MUIRadio />}
          value={optionValue}
          label={label}
        />
      ))
    }
  </RadioGroup>
);

RadioButtons.propTypes = radioButtonsPropertyTypes;

const radioButtonsWithControllerPropertyTypes = {
  control: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.shape({}).isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

type RadioButtonsWithControllerProperties<
  T extends FieldValues = FieldValues,
> = Omit<
  InferProps<typeof radioButtonsWithControllerPropertyTypes>,
  'control' | 'rules'
> & {
  control: Control<T>;
  rules?: UseControllerProps['rules'];
  values: { value: string; label: string }[];
};

export const RadioButtonsWithController = <
  T extends FieldValues = FieldValues,
>({
    control,
    name,
    rules,
    values,
  }: RadioButtonsWithControllerProperties<T>) => {
  const render = useCallback(
    ({
      field: { onChange, value },
    }: {

      field: ControllerRenderProps<T, FieldPath<T>>;
    }) => (
      <RadioButtons
        name={name}
        onChange={
          (
            { target: { value: selectedValue } },
          ) => onChange(selectedValue === 'true')
        }
        value={String(value)}
        values={values}
      />
    ),
    [name, values],
  );

  return renderController<T>({
    control, name, rules, render,
  });
};

RadioButtonsWithController.propTypes = radioButtonsWithControllerPropertyTypes;

export default RadioButtons;
